import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

type PassengerType = 'ADULT' | 'CHILD' | 'INFANT';

interface PassengerFormData {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  nationality?: string;
  passportNumber?: string;
  passportExpiry?: string;
  passengerType: PassengerType;
  isLeadPassenger: boolean;
  mealPreference?: string;
  specialNeeds?: string;
}

interface PassengerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  passenger?: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Date | null;
    nationality: string | null;
    passportNumber: string | null;
    passportExpiry: Date | null;
    passengerType: PassengerType;
    isLeadPassenger: boolean;
    mealPreference: string | null;
    specialNeeds: string | null;
  };
  onSuccess?: () => void;
}

function formatDateForInput(date: Date | null): string {
  if (!date) return '';
  return new Date(date).toISOString().split('T')[0] ?? '';
}

export function PassengerModal({
  open,
  onOpenChange,
  bookingId,
  passenger,
  onSuccess,
}: PassengerModalProps) {
  const { t } = useTranslation('bookings');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!passenger;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PassengerFormData>({
    defaultValues: passenger
      ? {
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          dateOfBirth: formatDateForInput(passenger.dateOfBirth),
          nationality: passenger.nationality ?? undefined,
          passportNumber: passenger.passportNumber ?? undefined,
          passportExpiry: formatDateForInput(passenger.passportExpiry),
          passengerType: passenger.passengerType,
          isLeadPassenger: passenger.isLeadPassenger,
          mealPreference: passenger.mealPreference ?? undefined,
          specialNeeds: passenger.specialNeeds ?? undefined,
        }
      : {
          passengerType: 'ADULT',
          isLeadPassenger: false,
        },
  });

  useEffect(() => {
    if (open) {
      if (passenger) {
        reset({
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          dateOfBirth: formatDateForInput(passenger.dateOfBirth),
          nationality: passenger.nationality ?? undefined,
          passportNumber: passenger.passportNumber ?? undefined,
          passportExpiry: formatDateForInput(passenger.passportExpiry),
          passengerType: passenger.passengerType,
          isLeadPassenger: passenger.isLeadPassenger,
          mealPreference: passenger.mealPreference ?? undefined,
          specialNeeds: passenger.specialNeeds ?? undefined,
        });
      } else {
        reset({
          passengerType: 'ADULT',
          isLeadPassenger: false,
        });
      }
    }
  }, [open, passenger, reset]);

  const utils = trpc.useUtils();

  const addMutation = trpc.booking.addPassenger.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(bookingId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.booking.updatePassenger.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(bookingId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: PassengerFormData) => {
    setIsSubmitting(true);

    if (isEditing && passenger) {
      updateMutation.mutate({
        id: passenger.id,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
        nationality: data.nationality,
        passportNumber: data.passportNumber,
        passportExpiry: data.passportExpiry ? new Date(data.passportExpiry) : undefined,
        passengerType: data.passengerType,
        isLeadPassenger: data.isLeadPassenger,
        mealPreference: data.mealPreference,
        specialNeeds: data.specialNeeds,
      });
    } else {
      addMutation.mutate({
        bookingId,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
        nationality: data.nationality,
        passportNumber: data.passportNumber,
        passportExpiry: data.passportExpiry ? new Date(data.passportExpiry) : undefined,
        type: data.passengerType,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('passengerModal.editPassenger') : t('passengerModal.addPassenger')}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('passengerModal.updateDescription')
              : t('passengerModal.addDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('passengerModal.firstName')} *</Label>
              <Input
                id="firstName"
                {...register('firstName', { required: t('passengerModal.firstNameRequired') })}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('passengerModal.lastName')} *</Label>
              <Input
                id="lastName"
                {...register('lastName', { required: t('passengerModal.lastNameRequired') })}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          {/* Type and Lead */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('passengerModal.passengerType')}</Label>
              <Combobox
                options={[
                  { value: 'ADULT', label: t('passengerModal.adult') },
                  { value: 'CHILD', label: t('passengerModal.child') },
                  { value: 'INFANT', label: t('passengerModal.infant') },
                ]}
                value={watch('passengerType') || 'ADULT'}
                onValueChange={(value) => setValue('passengerType', value as PassengerType)}
                placeholder={t('passengerModal.selectType')}
                searchPlaceholder={t('passengerModal.search')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">{t('passengerModal.dateOfBirth')}</Label>
              <Input
                id="dateOfBirth"
                type="date"
                {...register('dateOfBirth')}
              />
            </div>
          </div>

          {/* Passport Info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nationality">{t('passengerModal.nationality')}</Label>
              <Input
                id="nationality"
                {...register('nationality')}
                placeholder={t('passengerModal.nationalityPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passportNumber">{t('passengerModal.passportNumber')}</Label>
              <Input
                id="passportNumber"
                {...register('passportNumber')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="passportExpiry">{t('passengerModal.passportExpiry')}</Label>
            <Input
              id="passportExpiry"
              type="date"
              {...register('passportExpiry')}
            />
          </div>

          {/* Lead Passenger Checkbox */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isLeadPassenger"
                {...register('isLeadPassenger')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isLeadPassenger" className="font-normal">
                {t('passengerModal.leadPassenger')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('passengerModal.leadPassengerHint')}
            </p>
          </div>

          {/* Preferences */}
          <div className="space-y-2">
            <Label htmlFor="mealPreference">{t('passengerModal.mealPreference')}</Label>
            <Input
              id="mealPreference"
              {...register('mealPreference')}
              placeholder={t('passengerModal.mealPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="specialNeeds">{t('passengerModal.specialNeeds')}</Label>
            <Textarea
              id="specialNeeds"
              {...register('specialNeeds')}
              placeholder={t('passengerModal.specialNeedsPlaceholder')}
              rows={2}
            />
          </div>

          {/* Error Messages */}
          {(addMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {addMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('passengerModal.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t('passengerModal.saveChanges') : t('passengerModal.addPassenger')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
